import React from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { StockCpoPkData } from './types';
import { getCpoPkColor } from './colors';
import {
  Box,
  Heading,
  Text,
  useColorModeValue,
  Flex,
  useBreakpointValue,
} from '@chakra-ui/react';
import { formatNumber } from './utils';

interface StockCpoPkProps {
  data: StockCpoPkData[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  if (active && payload?.length) {
    return (
      <Box 
        bg={bgColor} 
        p={3} 
        borderWidth={1} 
        borderColor={borderColor} 
        borderRadius="md" 
        shadow="md"
      >
        <Box fontWeight="semibold">{`${label}`}</Box>
        {payload.map((entry: any) => (
          <Box key={`tooltip-${entry.name}`} fontSize="sm" style={{ color: entry.color }}>
            {`${entry.name}: ${formatNumber(entry.value)} ${entry.name.includes('FFA') ? '%' : 'kg'}`}
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

const StockCpoPk: React.FC<StockCpoPkProps> = ({ data }) => {
  const formatNumber = (num: number) => {
    return num.toLocaleString('id-ID', { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    });
  };

  // Transform data for chart
  const chartData = data.map(item => ({
    name: item.uraian,
    stock: item.stock,
    source: item.uraian === 'CPO' ? 'CPO' : 'PK'
  }));

  // Calculate totals
  const cpoData = data.find(d => d.uraian === 'CPO');
  const kernelData = data.find(d => d.uraian === 'PK');

  // Define common colors at the top level
  const bgCard = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');

  const rowHeight = useBreakpointValue({ 
      base: '110%',     // mobile - smaller
      md: '110%',       // tablet
      lg: '85%',       // laptop
      xl: '100%',      // desktop
      '2xl': '120%'    // large desktop
  });

  const chartMargin = useBreakpointValue({ 
      base: { top: 10, right: 10, left: 10, bottom: 5 },    // mobile - smaller margins
      md: { top: 15, right: 15, left: 15, bottom: 10 },     // tablet
      lg: { top: 20, right: 30, left: 20, bottom: 5 },      // laptop
      xl: { top: 20, right: 30, left: 20, bottom: 5 },      // desktop
      '2xl': { top: 25, right: 35, left: 25, bottom: 10 }   // large desktop
  });

  const xAxisHeight = useBreakpointValue({ 
      base: 60,        // mobile - smaller
      md: 70,          // tablet
      lg: 80,          // laptop
      xl: 80,          // desktop
      '2xl': 90        // large desktop
  });

  const yAxisFontSize = useBreakpointValue({ 
      base: 10,        // mobile - smaller
      md: 11,          // tablet
      lg: 12,          // laptop
      xl: 12,          // desktop
      '2xl': 14        // large desktop
  });

  const chartWidth = useBreakpointValue({ 
      base: "95%",     // mobile - almost full width
      md: "90%",       // tablet
      lg: "80%",       // laptop
      xl: "80%",       // desktop
      '2xl': "80%"     // large desktop
  });

  // Responsive minHeight for chart container
  const chartMinHeight = useBreakpointValue({
    base: '380px', // mobile
    md: '380px',   // tablet
    lg: '350px',   // laptop
    xl: '250px',   // desktop
    '2xl': '300px' // large desktop
  }) || '300px';

  return (
    <Box bg={bgCard} borderRadius="lg" p={4} h="100%" display="flex" flexDirection="column">
      <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px">
        STOCK CPO & PK
      </Heading>
      
      {/* Summary Cards */}
      <Flex align="center" gap={6} mb={4} mt={0} justify="center">
        <Text fontSize="sm" color="#15803D" fontWeight="semibold">
          Total CPO (kg): <Box as="span" fontWeight="bold" color="#15803D" display="inline">{cpoData ? formatNumber(cpoData.stock) : '-'}</Box>
        </Text>
        <Text fontSize="sm" color="#7C3AED" fontWeight="semibold">
          Total PK (kg): <Box as="span" fontWeight="bold" color="#7C3AED" display="inline">{kernelData ? formatNumber(kernelData.stock) : '-'}</Box>
        </Text>
      </Flex>

      {/* Chart */}
      <Box minH={chartMinHeight} h={chartMinHeight} w="100%" display="flex" justifyContent="center">
      {/* <Box minH="0" height="full" display="flex" justifyContent="center"> */}
        <ResponsiveContainer width="95%" height={rowHeight}>
          <ComposedChart
            data={chartData}
            margin={{ top: 15, right: 15, left: 15, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              fontSize={11}
              height={70}
            />
            <YAxis 
              yAxisId="left"
              orientation="left"
              fontSize={11}
              tickFormatter={formatNumber}
              label={{
                value: 'Berat (kg)',
                angle: -90,
                position: 'insideLeft',
                offset: -10,
                fontSize: 14,
                fill: '#666',
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              yAxisId="left"
              dataKey="stock"
              name="Stock"
            >
              {chartData.map((entry) => (
                <Cell key={`cell-${entry.name}`} fill={getCpoPkColor(entry.source)} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* Additional Info */}
      {/* <SimpleGrid columns={2} spacing={4} mt={4}>
        <Box bg={useColorModeValue('gray.50', 'gray.700')} borderWidth={1} borderColor={useColorModeValue('gray.200', 'gray.600')} borderRadius="lg" p={3}>
          <Text color={useColorModeValue('gray.600', 'gray.400')} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            FFA CPO
          </Text>
          <Text color={useColorModeValue('gray.900', 'gray.100')} fontSize="lg" fontWeight="bold">
            2.5%
          </Text>
          <Text color={useColorModeValue('gray.600', 'gray.400')} fontSize="xs">Konsentrasi</Text>
        </Box>
        
        <Box bg={useColorModeValue('gray.50', 'gray.700')} borderWidth={1} borderColor={useColorModeValue('gray.200', 'gray.600')} borderRadius="lg" p={3}>
          <Text color={useColorModeValue('gray.600', 'gray.400')} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            Moisture PK
          </Text>
          <Text color={useColorModeValue('gray.900', 'gray.100')} fontSize="lg" fontWeight="bold">
            0.8%
          </Text>
          <Text color={useColorModeValue('gray.600', 'gray.400')} fontSize="xs">Konsentrasi</Text>
        </Box>
      </SimpleGrid> */}
    </Box>
  );
};

export default StockCpoPk;
